import React from 'react';
import { 
  Target, Award, Clock, Brain, TrendingUp, Star, 
  Trophy, Users, ArrowUpRight, Zap, BookOpen, MessageSquare,
  CheckCircle
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const translations = {
  es: {
    sections: {
      progress: {
        title: 'Progreso y Logros',
        modules: 'Módulos Completados',
        skillLevel: 'Nivel de Habilidad'
      },
      performance: {
        title: 'Rendimiento',
        avgScore: 'Puntuación Media',
        latestScore: 'Última Evaluación',
        trend: 'Tendencia'
      },
      growth: {
        title: 'Mejora y Crecimiento',
        improvement: 'Mejora',
        strengths: 'Fortalezas',
        toImprove: 'Áreas de Mejora'
      },
      engagement: {
        title: 'Participación',
        xp: 'Puntos XP',
        nextLevel: 'Siguiente Nivel',
        rank: 'Posición'
      },
      time: {
        title: 'Gestión del Tiempo',
        total: 'Tiempo Total',
        recommended: 'Tiempo Recomendado',
        daily: 'Objetivo Diario'
      },
      feedback: {
        title: 'Feedback y Recomendaciones',
        latest: 'Último Feedback',
        nextSteps: 'Próximos Pasos',
        comparison: 'Comparación'
      }
    }
  },
  en: {
    sections: {
      progress: {
        title: 'Progress & Achievements',
        modules: 'Completed Modules',
        skillLevel: 'Skill Level'
      },
      performance: {
        title: 'Performance',
        avgScore: 'Average Score',
        latestScore: 'Latest Assessment',
        trend: 'Trend'
      },
      growth: {
        title: 'Improvement & Growth',
        improvement: 'Improvement',
        strengths: 'Strengths',
        toImprove: 'Areas to Improve'
      },
      engagement: {
        title: 'Engagement',
        xp: 'XP Points',
        nextLevel: 'Next Level',
        rank: 'Rank'
      },
      time: {
        title: 'Time Management',
        total: 'Total Time',
        recommended: 'Recommended Time',
        daily: 'Daily Goal'
      },
      feedback: {
        title: 'Feedback & Recommendations',
        latest: 'Latest Feedback',
        nextSteps: 'Next Steps',
        comparison: 'Comparison'
      }
    }
  }
};

type LearnerStatsDashboardProps = {
  stats: {
    progress: {
      overall: number;
      modules: { completed: number; total: number };
      skillLevel: string;
    };
    performance: {
      averageScore: number;
      latestScore: number;
      trend: 'up' | 'down' | 'stable';
    };
    engagement: {
      xp: number;
      nextLevelXp: number;
      rank: number;
      totalUsers: number;
    };
    time: {
      total: string;
      recommended: string;
      daily: string;
    };
    strengths: string[];
    improvements: string[];
    feedback: {
      latest: string;
      nextSteps: string[];
    };
  };
};

const LearnerStatsDashboard: React.FC<LearnerStatsDashboardProps> = ({ stats }) => {
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Progress & Achievements */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="dark-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-foreground/70">{t.sections.progress.modules}</p>
              <h3 className="text-2xl font-bold text-foreground mt-1">
                {stats.progress.modules.completed}/{stats.progress.modules.total}
              </h3>
            </div>
            <BookOpen className="h-8 w-8 text-green-500" />
          </div>
          <div className="mt-4">
            <div className="h-2 bg-secondary rounded-full">
              <div 
                className="h-2 bg-green-500 rounded-full transition-all duration-300"
                style={{ width: `${(stats.progress.modules.completed / stats.progress.modules.total) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="dark-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-foreground/70">{t.sections.progress.skillLevel}</p>
              <h3 className="text-2xl font-bold text-foreground mt-1">{stats.progress.skillLevel}</h3>
            </div>
            <Brain className="h-8 w-8 text-purple-500" />
          </div>
          <div className="mt-4 flex items-center space-x-1">
            {[1, 2, 3, 4, 5].map((level) => (
              <div
                key={level}
                className={`h-2 flex-1 rounded-full ${
                  level <= 3 ? 'bg-purple-500' : 'bg-secondary'
                }`}
              ></div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance & Engagement */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="dark-card p-6">
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Award className="h-5 w-5 text-accent mr-2" />
            {t.sections.performance.title}
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-foreground/70">{t.sections.performance.avgScore}</span>
              <div className="flex items-center">
                <span className="text-2xl font-bold text-foreground">{stats.performance.averageScore}</span>
                <span className="text-foreground/70 ml-1">/100</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground/70">{t.sections.performance.latestScore}</span>
              <div className="flex items-center">
                <span className="text-xl font-bold text-foreground">{stats.performance.latestScore}</span>
                <span className="text-foreground/70 ml-1">/100</span>
                {stats.performance.trend === 'up' && (
                  <ArrowUpRight className="h-4 w-4 text-green-500 ml-2" />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="dark-card p-6">
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Zap className="h-5 w-5 text-accent mr-2" />
            {t.sections.engagement.title}
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-foreground/70">{t.sections.engagement.xp}</span>
              <div className="flex items-center">
                <span className="text-2xl font-bold text-foreground">{stats.engagement.xp}</span>
                <span className="text-foreground/70 ml-2">XP</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground/70">{t.sections.engagement.rank}</span>
              <div className="flex items-center">
                <Trophy className="h-5 w-5 text-yellow-500 mr-2" />
                <span className="text-xl font-bold text-foreground">
                  Top {Math.round((stats.engagement.rank / stats.engagement.totalUsers) * 100)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Time Management & Feedback */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="dark-card p-6">
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Clock className="h-5 w-5 text-accent mr-2" />
            {t.sections.time.title}
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-foreground/70">{t.sections.time.total}</span>
              <span className="font-medium text-foreground">{stats.time.total}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground/70">{t.sections.time.daily}</span>
              <span className="font-medium text-foreground">{stats.time.daily}</span>
            </div>
          </div>
        </div>

        <div className="dark-card p-6">
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <MessageSquare className="h-5 w-5 text-accent mr-2" />
            {t.sections.feedback.title}
          </h3>
          <div className="space-y-4">
            <p className="text-foreground/70">{stats.feedback.latest}</p>
            <div className="space-y-2">
              {stats.feedback.nextSteps.map((step, index) => (
                <div key={index} className="flex items-center">
                  <ArrowUpRight className="h-4 w-4 text-accent mr-2" />
                  <span className="text-sm text-foreground">{step}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Strengths & Areas to Improve */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="dark-card p-6">
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Star className="h-5 w-5 text-yellow-500 mr-2" />
            {t.sections.growth.strengths}
          </h3>
          <div className="space-y-2">
            {stats.strengths.map((strength, index) => (
              <div key={index} className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                <span className="text-foreground">{strength}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="dark-card p-6">
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 text-accent mr-2" />
            {t.sections.growth.toImprove}
          </h3>
          <div className="space-y-2">
            {stats.improvements.map((improvement, index) => (
              <div key={index} className="flex items-center">
                <ArrowUpRight className="h-4 w-4 text-accent mr-2" />
                <span className="text-foreground">{improvement}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearnerStatsDashboard;